import { useEffect, useState } from "react";
import Button from "../../components/Button";
import Card from "../../components/Card";
import { scanTagUid } from "../../lib/nfc";
import { fetchNfcRequired, submitPunch } from "../../lib/punch";
import {
  useTenantPhone,
  phoneHref,
  type TenantPhoneState,
} from "../../lib/tenant-phone";
import { TENANT_PHONE } from "./constants";

interface PunchScreenProps {
  driverName: string;
  /**
   * SINGLE-SCAN-MORNINGS (C) — the UID scanned at the LOGIN gate, handed
   * through DriverApp. When present, CLOCK IN skips the scan sheet and
   * submits directly with it (one scan per session). CLOCK OUT ignores it —
   * an end-of-shift punch ALWAYS scans (PM model point 4). Optional: the
   * shift-aware landing gate (DriverApp) can reach this screen without a
   * login-scan (fresh NO_SHIFT session, e.g. device relink).
   */
  loginScanUid?: string | null;
  onDone: () => void;
  onKickout: () => void;
}

type ScreenState =
  | { mode: "idle" }
  | { mode: "scanning"; punchType: "IN" | "OUT"; scanAttempts: number }
  | { mode: "submitting"; punchType: "IN" | "OUT" };

export default function PunchScreen({
  driverName,
  loginScanUid,
  onDone,
  onKickout,
}: PunchScreenProps) {
  const [state, setState] = useState<ScreenState>({ mode: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [diagLines, setDiagLines] = useState<string[]>([]);
  // SPEC-47 Fix B — the machine code of the last failed punch, so the
  // no_open_shift 409 can render its help line. Hooks stay at top level.
  const [lastResultCode, setLastResultCode] = useState<string | null>(null);
  // SINGLE-SCAN-MORNINGS (C) — the handed UID as SCREEN-LOCAL state, consumed
  // at most once. A scan-failure retry must submit with the fresh scan only.
  const [handedUid, setHandedUid] = useState<string | null>(loginScanUid ?? null);
  const phoneState: TenantPhoneState = useTenantPhone();

  useEffect(() => {
    if (state.mode !== "scanning") {
      setDiagLines([]);
      return;
    }

    if (typeof window === "undefined") return;

    const interval = setInterval(() => {
      const log = ((window as any).__eagleNFCDiagLog || []) as string[];
      setDiagLines(log.slice(-4));
    }, 400);

    return () => clearInterval(interval);
  }, [state.mode]);

  const firstName = driverName.split(" ")[0];

  const handlePunchClick = async (punchType: "IN" | "OUT") => {
    setError(null);
    setLastResultCode(null);
    setState({ mode: "submitting", punchType });

    let nfcRequired: boolean;
    try {
      nfcRequired = await fetchNfcRequired();
    } catch {
      // Fail CLOSED: if we can't reach the config, assume NFC is required
      // (the safe default for a punch that must not silently skip the tag).
      // The scan UI will surface a bridge/scan error rather than submitting
      // a tagless punch the API would reject with a 400 anyway.
      nfcRequired = true;
    }

    // SINGLE-SCAN-MORNINGS (C) — CLOCK IN ONLY: a handed login-scan UID
    // skips the scan sheet entirely (one scan per session). CLOCK OUT NEVER
    // consults it — the end-of-shift punch always scans (PM model point 4).
    // The UID is consumed at the click, whichever branch runs. When the
    // office has NFC disabled, a tagUid on the wire is a guaranteed 400
    // nfc_disabled (timeclock punch route), so the handed UID is dropped and
    // the tagless path runs — no scan either way, same UX.
    if (punchType === "IN" && handedUid) {
      const uid = handedUid;
      setHandedUid(null);
      if (nfcRequired) {
        await executeSubmit(punchType, uid);
      } else {
        await executeSubmit(punchType);
      }
      return;
    }
    setHandedUid(null);

    if (nfcRequired) {
      startScanning(punchType, 1);
    } else {
      await executeSubmit(punchType);
    }
  };

  const startScanning = async (
    punchType: "IN" | "OUT",
    attemptNumber: number,
  ) => {
    setState({ mode: "scanning", punchType, scanAttempts: attemptNumber });
    setError(null);
    setLastResultCode(null);

    const result = await scanTagUid();
    if ("uid" in result) {
      setState({ mode: "submitting", punchType });
      await executeSubmit(punchType, result.uid);
    } else {
      if (result.error === "cancelled") {
        setError("Scan cancelled. Tap Try Again or go Back.");
        setState({ mode: "scanning", punchType, scanAttempts: attemptNumber });
        return;
      }
      setError(`Scan failed: ${result.error}.`);
      setState({ mode: "scanning", punchType, scanAttempts: attemptNumber });
    }
  };

  const executeSubmit = async (punchType: "IN" | "OUT", tagUid?: string) => {
    setState({ mode: "submitting", punchType });
    const res = await submitPunch(punchType, tagUid);
    if (res.ok) {
      onDone();
    } else if (res.kind === "invalid_tag") {
      onKickout();
    } else if (res.kind === "auth") {
      onKickout();
    } else {
      // SPEC-47 Fix B: a 409 no_open_shift lands here (kind "error"). NO
      // kickout — the driver stays on the screen, reads the message, and can
      // retry or tap CLOCK IN. The code is kept so the help line can render.
      setError(res.message || "Failed to submit punch. Please try again.");
      setLastResultCode(res.code ?? null);
      setState({ mode: "idle" });
    }
  };

  /**
   * Office-phone help line for the no-open-shift case (SPEC-47 §2.3, the
   * useTenantPhone pattern from DispatchNfcGate / WaitingScreen). Loading and
   * fallback both paint the tappable constant so the driver is never left
   * without a number in a dead zone; an explicit `ok: null` means the office
   * cleared the phone, so it renders as not-configured instead.
   */
  const renderNoOpenShiftHelp = () => {
    const shown =
      phoneState.status === "ok" && phoneState.phone
        ? phoneState.phone
        : TENANT_PHONE;
    if (phoneState.status === "ok" && !phoneState.phone) {
      return (
        <span className="muted" style={{ fontSize: "13px" }}>
          Office phone not configured
        </span>
      );
    }
    return (
      <a href={phoneHref(shown)} className="contact-link" style={{ fontSize: "13px" }}>
        Not clocked in? Call the office ({shown})
      </a>
    );
  };

  const isBusy = state.mode === "submitting";

  return (
    <main className="driver-login">
      <Card>
        <h1>Eagle Dispatch</h1>
        <p className="sub">Welcome, {firstName}</p>

        {state.mode === "scanning" ? (
          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <h2 style={{ fontSize: "18px", color: "var(--gold)" }}>
              Hold phone near tag
            </h2>
            <p className="muted" style={{ margin: "12px 0 20px" }}>
              Scanning breakroom NFC tag for Clock{" "}
              {state.punchType === "IN" ? "In" : "Out"}…
            </p>

            {error && <p className="form-error" style={{ marginBottom: 16 }}>{error}</p>}

            {state.scanAttempts >= 3 && (
              <p className="muted" style={{ fontSize: "13px", marginBottom: "16px" }}>
                Having trouble scanning? Contact dispatch or office for assistance.
              </p>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Button
                variant="primary"
                onClick={() => startScanning(state.punchType, state.scanAttempts + 1)}
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setError(null);
                  setLastResultCode(null);
                  setState({ mode: "idle" });
                }}
              >
                ← Back
              </Button>
            </div>

            {diagLines.length > 0 && (
              <div
                style={{
                  marginTop: 16,
                  fontSize: 12,
                  opacity: 0.7,
                  textAlign: "left",
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                }}
              >
                {diagLines.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: "24px" }}>
            <p className="muted" style={{ textAlign: "center", marginBottom: "20px" }}>
              Select an action to continue:
            </p>

            {error && <p className="form-error" style={{ marginBottom: 16 }}>{error}</p>}

            {/* SPEC-47 Fix B — no_open_shift is a recoverable 409: the driver
                stays on this screen (no kickout) and gets the office phone. */}
            {error && lastResultCode === "no_open_shift" && (
              <p style={{ textAlign: "center", margin: "-8px 0 16px" }}>
                {renderNoOpenShiftHelp()}
              </p>
            )}

            <div className="home-buttons">
              <button
                type="button"
                className="btn btn-primary home-button"
                disabled={isBusy}
                onClick={() => void handlePunchClick("IN")}
                style={{ width: "100%" }}
              >
                <span className="home-button-title">CLOCK IN</span>
                <span className="home-button-hint">Start work shift</span>
              </button>

              <button
                type="button"
                className="btn btn-secondary home-button"
                disabled={isBusy}
                onClick={() => void handlePunchClick("OUT")}
                style={{ width: "100%" }}
              >
                <span className="home-button-title">CLOCK OUT</span>
                <span className="home-button-hint">End work shift</span>
              </button>
            </div>

            {isBusy && (
              <p className="muted" style={{ textAlign: "center", marginTop: 16 }}>
                Submitting punch…
              </p>
            )}
          </div>
        )}
      </Card>
    </main>
  );
}
